import { useCallback } from "react";
import useHttp from "./useHttp";

export default function useTemplates() {
  const { fetchData } = useHttp();

  var urlParams = new URLSearchParams(window.location.search);
  var rightServiceId = urlParams.get("rightServiceId");

  const fetchSavedTemplates = useCallback(
    (appProperties, integrationId, includeKey, sortBy, sortDirection) => {
      return new Promise((resolve, reject) => {
        let integId = integrationId ?? appProperties?.randomIntegId;
        let targetUrl = "templates";

        // Build URL with parameters
        const params = [];
        if (integId) {
          params.push(`integId=${integId}`);
        }
        if (includeKey) {
          params.push(`includeKey=${includeKey}`);
        }
        if (sortBy) {
          params.push(`sortBy=${sortBy}`);
        }
        // Only add asc parameter if sortBy is provided
        if (sortBy && sortDirection !== undefined) {
          params.push(`asc=${sortDirection}`);
        }

        // Append parameters to URL
        if (params.length > 0) {
          targetUrl += `?${params.join('&')}`;
        }

        if (appProperties) {
          fetchData(targetUrl, "GET", null, appProperties).then((response) => {
            let responseData = "";
            if (response) {
              responseData = JSON.parse(response);
            }
            let savedTemplates = responseData?.data;

            resolve(savedTemplates);
          });
        }
      });
    },
    [fetchData]
  );

  const fetchTemplatesByChannel = useCallback(
    (integId, appProperties, channel) => {
      return new Promise((resolve, reject) => {
        fetchData(`${channel}/templates?integId=${integId}`, "GET", null, appProperties).then((response) => {
          if (response) {
            response = JSON.parse(response);
            resolve(response?.data);
          }
        });
      });
    },
    [fetchData]
  );

  const fetchSavedTemplatesByModule = useCallback((appProperties, moduleName) => {
    return new Promise((resolve, reject) => {
      let integId = appProperties.randomIntegId;

      if (appProperties !== undefined) {
        fetchData(`templates?module=${moduleName}`, "GET", null, appProperties).then((response) => {
          let responseData = "";
          if (response) {
            responseData = JSON.parse(response);
          }
          let savedTemplates = responseData?.data;

          resolve(savedTemplates);
        });
      }
    });
  }, [fetchData]);

  const fetchModules = useCallback(
    (appProperties, includeFilters, action) => {
      return new Promise((resolve, reject) => {
        if (appProperties.leftServiceId !== undefined) {
          let targetUrl = `application/modules?leftServiceId=${appProperties.leftServiceId}&action=${action}&rightServiceId=${rightServiceId}`;
          targetUrl += includeFilters ? `&includeFilters=true` : ``;
          fetchData(targetUrl, "GET", null, appProperties).then((response) => {
            let responseData = "";
            if (response) {
              responseData = JSON.parse(response);
            }
            let modules = responseData?.data;

            resolve(modules);
          });
        }
      });
    },
    [fetchData]
  );
  const fetchFiltersByModule = useCallback(
    (appProperties, module, fromNumber) => {
      let encodedFromNumber = encodeURIComponent(fromNumber);
      let integId = appProperties?.installedApps?.[0]?.integProps?.integId;
      return new Promise((resolve, reject) => {
        if (appProperties !== undefined) {
          fetchData(`${integId}/campaign/filters?moduleName=${module}&fromNumber=${encodedFromNumber}`, "GET", null, appProperties).then(
            (response) => {
              let responseData = "";
              if (response) {
                responseData = JSON.parse(response);
              }
              let filters = responseData?.data;

              resolve(filters);
            }
          );
        }
      });
    },
    [fetchData]
  );

  const fetchFields = useCallback(
    (appProperties, moduleName) => {
      return new Promise((resolve, reject) => {
        if (appProperties && appProperties?.leftServiceId) {
          fetchData(
            `module/fields?moduleName=${moduleName}&leftServiceId=${appProperties.leftServiceId}&lookupFields=true&rightServiceId=${rightServiceId}`,
            "GET",
            null,
            appProperties
          ).then((response) => {
            let responseData = "";
            if (response) {
              responseData = JSON.parse(response);
            }
            let fields = responseData?.data;

            resolve(fields);
          });
        }
      });
    },
    [fetchData]
  );

  const fetchAllFields = useCallback(
    (appProperties, moduleName, lookupFields) => {
      let integId = appProperties.randomIntegId;
      return new Promise((resolve, reject) => {
        if (appProperties !== undefined && appProperties?.leftServiceId) {
          var urlParams = new URLSearchParams(window.location.search);
          var rightServiceId = urlParams.get("rightServiceId");
          fetchData(
            `module/fields?leftServiceId=${appProperties.leftServiceId}&moduleName=${moduleName}&lookupFields=true&rightServiceId=${rightServiceId}`,
            "GET",
            null,
            appProperties
          ).then((response) => {
            let responseData = "";
            if (response) {
              responseData = JSON.parse(response);
            }
            let fields = responseData?.data;

            resolve(fields);
          });
        }
      });
    },
    [fetchData]
  );

  const fetchPhoneFieldsWithValues = useCallback(
    (appProperties, moduleName, recordId) => {
      return new Promise((resolve, reject) => {
        if (appProperties !== undefined) {
          var urlParams = new URLSearchParams(window.location.search);
          var rightServiceId = urlParams.get("rightServiceId");
          var leftId = urlParams.get("leftServiceId");

          var leftServiceId = leftId || appProperties.leftServiceId;
          fetchData(
            `app/phone-fields/values?leftServiceId=${leftServiceId}&moduleName=${moduleName}&recordId=${recordId}&rightServiceId=${rightServiceId}`,
            "GET",
            null,
            appProperties
          ).then((response) => {
            let responseData = "";
            if (response) {
              responseData = JSON.parse(response);
            }
            let fields = responseData?.data;

            resolve(fields);
          });
        }
      });
    },
    [fetchData]
  );
  const fetchOnlyPhoneFields = useCallback(
    (appProperties, moduleName) => {
      return new Promise((resolve, reject) => {
        if (appProperties !== undefined) {
          var urlParams = new URLSearchParams(window.location.search);
          var rightServiceId = urlParams.get("rightServiceId");
          var leftId = urlParams.get("leftServiceId");

          var leftServiceId = leftId || appProperties.leftServiceId;
          fetchData(
            `app/phone-fields/values?leftServiceId=${leftServiceId}&moduleName=${moduleName}&rightServiceId=${rightServiceId}`,
            "GET",
            null,
            appProperties
          ).then((response) => {
            let responseData = "";
            if (response) {
              responseData = JSON.parse(response);
            }
            let fields = responseData?.data;

            resolve(fields);
          });
        }
      });
    },
    [fetchData]
  );

  const addTemplate = (appProperties, body, integrationId) => {
    return new Promise((resolve, reject) => {
      let integId = integrationId ?? appProperties.randomIntegId;
      let targetUrl = `template?leftServiceId=${appProperties.leftServiceId ?? ""}&integId=${integId ?? ""}`;

      if (appProperties !== undefined) {
        fetchData(targetUrl, "POST", body, appProperties).then((response) => {
          let responseData = "";
          if (response) {
            responseData = JSON.parse(response);
          }
          let addedTemp = responseData?.data;

          resolve(addedTemp);
        });
      }
    });
  };

  const updateTemplate = (appProperties, body, templateId) => {
    return new Promise((resolve, reject) => {
      let integId = appProperties.randomIntegId;

      if (appProperties !== undefined) {
        fetchData(`template/${templateId}`, "PUT", body, appProperties).then((response) => {
          let responseData = "";
          if (response) {
            responseData = JSON.parse(response);
          }
          let addedTemp = responseData?.data;

          resolve(addedTemp);
        });
      }
    });
  };
  const deleteTemplate = (appProperties, templateId) => {
    return new Promise((resolve, reject) => {
      let integId = appProperties.randomIntegId;

      if (appProperties !== undefined) {
        fetchData(`template/${templateId}?leftServiceId=${appProperties.leftServiceId}`, "DELETE", null, appProperties).then((response) => {
          let responseData = "";
          if (response) {
            responseData = JSON.parse(response);
          }
          let deleteTemp = responseData?.data;

          resolve(deleteTemp);
        });
      }
    });
  };

  const fetchFilterInfoByFilterId = (
    appProperties,
    integId,
    moduleName,
    filterId,
    selectedIds,
    excludedIds,
    leftServiceId,
    filterType,
    filter,
    signal = null
  ) => {
    return new Promise((resolve, reject) => {
      let targetUrl = `filter/records/count?moduleName=${moduleName}&filterId=${filterId}`;

      if (filterType) {
        targetUrl += `&filterType=${filterType}`;
      }
      if (leftServiceId) {
        targetUrl += `&leftServiceId=${leftServiceId}`;
      }
      if (integId) {
        targetUrl += `&integId=${integId}`;
      }
      if (selectedIds) {
        targetUrl += `&selectedIds=${selectedIds}`;
      }
      if (excludedIds) {
        targetUrl += `&excludedIds=${excludedIds}`;
      }

      if (filter) {
        targetUrl += `&filter=${encodeURIComponent(filter)}`;
      }

      fetchData(targetUrl, "GET", null, appProperties, null, null, signal)
        .then((res) => {
          if (res) {
            const responseJson = JSON.parse(res);
            if (responseJson?.data) {
              resolve(responseJson.data);
            }
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const fetchTemplateById = (integId, templateId, templateName, type, appProperties, wabaId) => {
    return new Promise(async (resolve, reject) => {
      try {
        let targetUrl = `omessage/${integId}/template/${templateId}`;
        if (type) {
          targetUrl += `?type=${type}`;
        }
        if (templateName && wabaId) {
          targetUrl += `&template_name=${templateName}&waba_id=${wabaId}`;
        }
        const response = await fetchData(targetUrl, "GET", null, appProperties, null);
        if (response) {
          const responseJson = JSON.parse(response);
          const responseJsonData = responseJson?.data;
          if (responseJsonData?.success) {
            resolve(responseJsonData?.data);
          } else {
            reject(responseJsonData);
          }
        }
      } catch (e) {
        reject(e);
      }
    });
  };

  return {
    fetchSavedTemplates,
    fetchModules,
    fetchFields,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    fetchSavedTemplatesByModule,
    fetchAllFields,
    fetchFiltersByModule,
    fetchFilterInfoByFilterId,
    fetchTemplateById,
    fetchPhoneFieldsWithValues,
    fetchTemplatesByChannel,
    fetchOnlyPhoneFields,
  };
}
